/**
 * Life4Earth V2 — Waitlist & Invitation API (DEV-144)
 *
 * Admin-only endpoints for managing the registration waitlist
 * and sending user invitations.
 */

import { apiFetch } from './client';

// ── Types ────────────────────────────────────────────────────

export interface WaitlistEntry {
  id: string;
  email: string;
  displayName: string | null;
  source: string;
  status: string;
  adminNote: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface WaitlistPage {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  entries: WaitlistEntry[];
}

export interface WaitlistCounts {
  [status: string]: number;
}

// ── API calls ────────────────────────────────────────────────

export async function fetchWaitlist(
  status = 'pending',
  page = 1,
  pageSize = 50,
): Promise<WaitlistPage> {
  const params = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize) });
  const result = await apiFetch<WaitlistPage>(`/api/admin/waitlist?${params}`);
  return result!;
}

export async function fetchWaitlistCounts(): Promise<WaitlistCounts> {
  const result = await apiFetch<WaitlistCounts>('/api/admin/waitlist/counts');
  return result ?? {};
}

export async function approveWaitlistEntry(id: string, note?: string): Promise<void> {
  await apiFetch(`/api/admin/waitlist/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({ note: note ?? null }),
  });
}

export async function denyWaitlistEntry(id: string, note?: string): Promise<void> {
  await apiFetch(`/api/admin/waitlist/${id}/deny`, {
    method: 'POST',
    body: JSON.stringify({ note: note ?? null }),
  });
}

export async function batchApproveWaitlist(ids: string[]): Promise<{ approved: number; total: number }> {
  const result = await apiFetch<{ approved: number; total: number }>('/api/admin/waitlist/batch-approve', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  return result!;
}

export async function inviteUser(email: string, displayName?: string): Promise<void> {
  await apiFetch('/api/admin/waitlist/invite', {
    method: 'POST',
    body: JSON.stringify({ email, displayName: displayName ?? null }),
  });
}
